<template>
  <div class="amap-address-selector">
    <!-- 输入框区域 -->
    <div class="address-input-wrapper">
      <div style="display: contents">
        <el-input v-model="displayAddress" :placeholder="placeholder || '请点击选择地址'" class="address-input"
          style="width: calc(100% - 135px); margin-right: 10px;" @input="addressInput">
        </el-input>
      </div>
      <el-button type="primary" size="small" @click="openMapDialog">定位</el-button>
      <el-button type="danger" size="small" @click="handleClear" v-if="displayAddress">清空</el-button>
    </div>

    <!-- 地图选址弹窗 -->
    <el-dialog :visible.sync="dialogVisible" title="选择地址" fullscreen :before-close="closeDialog" class="map-dialog"
      append-to-body>
      <div class="map-container" style="position: relative;">
        <el-button type="primary" @click="toggleSatellite" style="position:absolute; bottom: 50px; z-index: 9999;
          right: 20px;" size="small">{{ isSatelliteMode ? '切换普通地图' : '切换卫星地图' }} </el-button>
        <!-- 搜索框 -->
        <div class="search-wrapper" v-if="dialogVisible">
          <el-input v-model="searchKeyword" placeholder="请输入省市区及关键词进行搜索" class="search-input"
            @keyup.enter.native="searchAddress" @input="onSearchInput" @focus="showSuggestions = true"
            @blur="hideSuggestions">
            <i slot="append" class="el-icon-search" @click="searchAddress"></i>
          </el-input>

          <!-- 地址联想列表 -->
          <div v-if="showSuggestions && suggestions.length > 0" class="suggestions-list" v-loading="loadingSuggestions">
            <div v-for="(item, index) in suggestions" :key="index" class="suggestion-item"
              @mousedown="selectSuggestion(item)">
              <div class="suggestion-name">{{ item.name }}</div>
              <div class="suggestion-address">{{ item.address }}</div>
            </div>
          </div>
        </div>

        <!-- 地图容器 -->
        <div :id="mapContainerId" class="map-wrapper" v-loading="mapLoading"></div>
        <div style="background: rgba(225,222,225,0.42); color: #111111; padding: 10px;" v-if="selectedLocation.address">
          已选地址：{{ selectedLocation.address }}
        </div>
      </div>

      <div slot="footer">
        <el-button @click="closeDialog">取消</el-button>
        <el-button type="primary" @click="confirmSelection"
          :disabled="!selectedLocation.longitude || !selectedLocation.latitude">
          确定
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import AMapLoader from '@amap/amap-jsapi-loader'

export default {
  name: 'AmapAddressSelector',
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    modelData: {
      type: Object,
      default: () => ({})
    },
    placeholder: {
      type: String,
      default: '请点击选择地址'
    },
    center: {
      type: Array,
      default: () => [116.404, 39.915]
    },
    zoom: {
      type: Number,
      default: 13
    }
  },
  data() {
    return {
      displayAddress: '',
      dialogVisible: false,
      searchKeyword: '',
      mapLoading: false,
      mapContainerId: `amapContainer_${Date.now()}_${Math.random()}`,
      showSuggestions: false,
      suggestions: [],
      loadingSuggestions: false,
      suggestionTimer: null,
      selectedLocation: {
        province: '',
        city: '',
        district: '',
        address: '',
        longitude: 0,
        latitude: 0
      },
      AMap: null,
      map: null,
      marker: null,
      geocoder: null,
      placeSearch: null,
      isSatelliteMode: false,
      AMAP_API_KEY: 'd003d46adae9ea23d0e315bec8309686',
      AMAP_SECURITY_KEY: 'ef95e7451a4ce89c2d00020f1a0198b2'
    }
  },
  watch: {
    modelValue: {
      handler(newVal) {
        this.displayAddress = newVal || ''
      },
      immediate: true
    },
    modelData: {
      handler(newVal) {
        if (newVal && typeof newVal === 'object') {
          Object.assign(this.selectedLocation, newVal)
        }
      },
      immediate: true,
      deep: true
    }
  },
  mounted() {
    if (this.modelValue) {
      this.displayAddress = this.modelValue
    }
    if (this.modelData && typeof this.modelData === 'object') {
      Object.assign(this.selectedLocation, this.modelData)
    }
  },
  beforeDestroy() {
    if (this.map) {
      this.map.destroy()
      this.map = null
    }
    if (this.suggestionTimer) {
      clearTimeout(this.suggestionTimer)
      this.suggestionTimer = null
    }
    this.AMap = null
    this.geocoder = null
    this.marker = null
    this.placeSearch = null
  },
  methods: {
    toggleSatellite() {
      if (!this.map) return
      this.$set(this, 'isSatelliteMode', !this.isSatelliteMode)
      if (this.isSatelliteMode) {
        this.map.setLayers([
          new AMap.TileLayer.Satellite(),
          new AMap.TileLayer.RoadNet()
        ])
      } else {
        this.map.setLayers([new AMap.TileLayer()])
      }
    },
    openMapDialog() {
      this.dialogVisible = true
      this.isSatelliteMode = false
      this.$nextTick(() => {
        if (this.modelData && this.modelData.address) {
          this.selectedLocation = {
            province: this.modelData.province,
            city: this.modelData.city,
            district: this.modelData.district,
            address: this.displayAddress,
            longitude: this.modelData.longitude || 0,
            latitude: this.modelData.latitude || 0
          }
        }
        this.initAMap()
      })
    },

    async initAMap() {
      try {
        this.mapLoading = true
        await this.$nextTick()
        const mapContainer = document.getElementById(this.mapContainerId)
        if (!mapContainer) {
          setTimeout(() => this.initAMap(), 200)
          return
        }

        window._AMapSecurityConfig = {
          securityJsCode: this.AMAP_SECURITY_KEY
        }

        this.AMap = await AMapLoader.load({
          key: this.AMAP_API_KEY,
          version: '2.0',
          plugins: ['AMap.Geocoder', 'AMap.PlaceSearch', 'AMap.TileLayer.Satellite', 'AMap.TileLayer.RoadNet']
        })

        this.map = new this.AMap.Map(this.mapContainerId, {
          zoom: this.zoom,
          center: this.center,
          viewMode: '2D',
          layers: [new AMap.TileLayer()]
        })

        try {
          this.geocoder = new this.AMap.Geocoder({ radius: 500, extensions: 'all' })
        } catch (e) {
          this.geocoder = null
        }

        try {
          this.placeSearch = new this.AMap.PlaceSearch({ pageSize: 8, pageIndex: 1, autoFitView: true })
        } catch (e) {
          this.placeSearch = null
        }

        this.map.on('click', this.onMapClick)

        if (this.selectedLocation.longitude && this.selectedLocation.latitude) {
          const point = [this.selectedLocation.longitude, this.selectedLocation.latitude]
          this.addMarker(point)
          this.map.setCenter(point)
        }
      } catch (error) {
        this.$message.error({ showClose: true, message: '地图加载失败，请稍后重试' })
      } finally {
        this.mapLoading = false
      }
    },

    onMapClick(e) {
      const lnglat = e.lnglat
      if (!lnglat) return
      const lng = lnglat.lng !== undefined ? lnglat.lng : lnglat.getLng()
      const lat = lnglat.lat !== undefined ? lnglat.lat : lnglat.getLat()
      this.selectedLocation.longitude = Number(lng || 0)
      this.selectedLocation.latitude = Number(lat || 0)
      const point = [this.selectedLocation.longitude, this.selectedLocation.latitude]
      this.addMarker(point)
      this.parseAddressFromCoordinates(point, 'click')
    },

    parseAddressFromCoordinates(point, type = 'search') {
      if (!this.geocoder) {
        this.updateLocationBasicInfo(point, null)
        this.$message.warning({ showClose: true, message: '地理编码服务不可用，仅保存坐标信息' })
        return
      }
      try {
        this.geocoder.getAddress(point, (status, result) => {
          if (status === 'complete' && result && result.regeocode) {
            const regeocode = result.regeocode
            const addressComponent = regeocode.addressComponent
            this.selectedLocation.province = addressComponent.province
            this.selectedLocation.city = addressComponent.city
            this.selectedLocation.district = addressComponent.district
            if (type === 'search') {
              this.handleSetAddress(this.selectedLocation.address)
            } else {
              this.handleSetAddress(regeocode.formattedAddress)
            }
          } else {
            this.updateLocationBasicInfo(point, null)
            if (status === 'no_data') {
              this.$message.warning({ showClose: true, message: '该位置无法获取详细地址信息' })
            } else if (status === 'error') {
              this.$message.error({ showClose: true, message: '地址解析服务异常,请稍后重试' })
            } else {
              this.$message.warning({ showClose: true, message: '地址解析失败,仅保存坐标信息' })
            }
          }
        })
      } catch (error) {
        this.updateLocationBasicInfo(point, null)
        this.$message.error({ showClose: true, message: '地址解析服务异常' })
      }
    },

    handleSetAddress(address) {
      const loc = this.selectedLocation
      const fields = [loc.province, loc.city, loc.district].filter(Boolean)
      fields.sort((a, b) => b.length - a.length)
      fields.forEach(field => {
        const regex = new RegExp(field, 'g')
        address = address.replace(regex, '')
      })
      address = address.replace(/\s+/g, ' ').trim()
      this.selectedLocation.address = [loc.province, loc.city, loc.district, address].filter(Boolean).join(' ')
    },

    updateLocationBasicInfo(point, fallbackAddress) {
      this.selectedLocation.longitude = point[0]
      this.selectedLocation.latitude = point[1]
      this.selectedLocation.province = ''
      this.selectedLocation.city = ''
      this.selectedLocation.district = ''
      this.selectedLocation.address = fallbackAddress || '已选择位置'
    },

    addMarker(point) {
      if (!this.map || !this.AMap) return
      if (this.marker) {
        this.map.remove(this.marker)
      }
      this.marker = new this.AMap.Marker({
        position: point,
        draggable: false
      })
      this.map.add(this.marker)
    },

    searchAddress() {
      if (!this.searchKeyword.trim()) {
        this.$message.warning({ showClose: true, message: '请输入搜索关键词' })
        return
      }
      if (this.suggestions.length > 0) {
        this.selectSuggestion(this.suggestions[0])
        return
      }
      this.$message.warning({ showClose: true, message: '请等待搜索联想或检查关键词' })
    },

    async getAddressCoordinates(address) {
      try {
        const url = `https://restapi.amap.com/v3/geocode/geo?address=${encodeURIComponent(address)}&key=f72d5bf159a582bacbbf2f5f5b80b8f5`
        const response = await fetch(url)
        const data = await response.json()
        if (data.status === '1' && data.geocodes && data.geocodes.length > 0) {
          const geocode = data.geocodes[0]
          const [lng, lat] = geocode.location.split(',').map(Number)
          return {
            name: geocode.formatted_address || address,
            address: geocode.formatted_address || address,
            location: { lng, lat },
            pname: geocode.province || '',
            cityname: geocode.city || '',
            adname: geocode.district || '',
            isGeocodeResult: true,
            geocodeData: geocode
          }
        }
        return null
      } catch (error) {
        return null
      }
    },

    onSearchInput() {
      const keyword = this.searchKeyword.trim()
      if (!keyword || keyword.length < 2) {
        this.suggestions = []
        this.showSuggestions = false
        return
      }
      if (!this.placeSearch) return

      if (this.suggestionTimer) {
        clearTimeout(this.suggestionTimer)
      }
      this.suggestionTimer = setTimeout(async () => {
        this.loadingSuggestions = true
        try {
          const geocodeResult = await this.getAddressCoordinates(keyword)
          this.placeSearch.search(keyword, (status, result) => {
            let finalSuggestions = []
            if (geocodeResult) {
              finalSuggestions.push({
                name: geocodeResult.name,
                address: geocodeResult.address,
                location: geocodeResult.location,
                poi: geocodeResult,
                isGeocodeResult: true
              })
            }
            if (status === 'complete' && result.poiList && result.poiList.pois) {
              const poiSuggestions = result.poiList.pois.slice(0, 5).map(poi => ({
                name: poi.name,
                address: poi.address || `${poi.pname || ''}${poi.cityname || ''}${poi.adname || ''}`,
                location: poi.location,
                poi: poi,
                isGeocodeResult: false
              }))
              finalSuggestions = finalSuggestions.concat(poiSuggestions)
            }
            this.suggestions = finalSuggestions
            this.showSuggestions = finalSuggestions.length > 0
            this.loadingSuggestions = false
          })
        } catch (error) {
          this.loadingSuggestions = false
          this.placeSearch.search(keyword, (status, result) => {
            if (status === 'complete' && result.poiList && result.poiList.pois) {
              this.suggestions = result.poiList.pois.slice(0, 6).map(poi => ({
                name: poi.name,
                address: poi.address || `${poi.pname || ''}${poi.cityname || ''}${poi.adname || ''}`,
                location: poi.location,
                poi: poi,
                isGeocodeResult: false
              }))
              this.showSuggestions = this.suggestions.length > 0
            } else {
              this.suggestions = []
              this.showSuggestions = false
            }
          })
        }
      }, 400)
    },

    hideSuggestions() {
      setTimeout(() => {
        this.showSuggestions = false
      }, 200)
    },

    selectSuggestion(item) {
      this.searchKeyword = item.name
      this.showSuggestions = false
      const point = [item.location.lng, item.location.lat]
      this.map.setCenter(point)
      this.map.setZoom(15)
      this.selectedLocation.longitude = Number(point[0])
      this.selectedLocation.latitude = Number(point[1])
      this.addMarker(point)
      this.updateLocationDataFromPOI(point, item.poi)
    },

    updateLocationDataFromPOI(point, poi) {
      this.selectedLocation.longitude = Number(point[0])
      this.selectedLocation.latitude = Number(point[1])
      this.selectedLocation.address = poi.name
      this.parseAddressFromCoordinates(point)
    },

    closeDialog() {
      this.dialogVisible = false
      this.searchKeyword = ''
      this.mapLoading = false
      this.showSuggestions = false
      this.suggestions = []
      if (this.suggestionTimer) {
        clearTimeout(this.suggestionTimer)
        this.suggestionTimer = null
      }
    },

    confirmSelection() {
      if (!this.selectedLocation.longitude || !this.selectedLocation.latitude) {
        this.$message.warning({ showClose: true, message: '请先在地图上选择位置' })
        return
      }
      const addressText =
        this.selectedLocation.address ||
        `已选择位置(${this.selectedLocation.longitude.toFixed(6)}, ${this.selectedLocation.latitude.toFixed(6)})`

      this.displayAddress = addressText
      this.$emit('update-modelData', {
        province: this.selectedLocation.province || '',
        city: this.selectedLocation.city || '',
        district: this.selectedLocation.district || '',
        address: this.selectedLocation.address || addressText,
        longitude: this.selectedLocation.longitude,
        latitude: this.selectedLocation.latitude
      })
      this.$message.success({ showClose: true, message: '地址选择成功' })
      this.closeDialog()
    },

    handleClear() {
      this.displayAddress = ''
      this.selectedLocation = {
        province: '',
        city: '',
        district: '',
        address: '',
        longitude: 0,
        latitude: 0
      }
      this.$emit('update-modelData', this.selectedLocation)
    },
    addressInput() {
      this.$emit('update-modelData', {
        province: this.selectedLocation.province || '',
        city: this.selectedLocation.city || '',
        district: this.selectedLocation.district || '',
        address: this.displayAddress,
        longitude: this.selectedLocation.longitude,
        latitude: this.selectedLocation.latitude
      })
    }
  }
}
</script>

<style scoped>
/* 样式部分完全保留，无需修改 */
.amap-address-selector {
  width: 100%;
}

.address-input-wrapper {
  width: 100%;
}

.address-input {
  width: 100%;
}

.location-icon {
  cursor: pointer;
  color: #409eff;
}

.location-icon:hover {
  color: #66b1ff;
}

.map-dialog .map-container {
  display: flex;
  flex-direction: column;
}

.map-dialog .search-wrapper {
  margin-bottom: 12px;
  position: relative;
}

.map-dialog .search-input {
  width: 100%;
}

.map-dialog .suggestions-list {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 9999;
  background: white;
  border: 1px solid #dcdfe6;
  border-top: none;
  border-radius: 0 0 4px 4px;
  max-height: 160px;
  overflow-y: auto;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.map-dialog .suggestion-item {
  padding: 6px 10px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  font-size: 13px;
}

.map-dialog .suggestion-item:hover {
  background-color: #f5f7fa;
}

.map-dialog .suggestion-item:last-child {
  border-bottom: none;
}

.map-dialog .suggestion-name {
  font-size: 13px;
  color: #303133;
  margin-bottom: 2px;
  font-weight: 500;
}

.map-dialog .suggestion-address {
  font-size: 11px;
  color: #909399;
}

.map-dialog .map-wrapper {
  flex: 1;
  min-height: 400px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background-color: #f5f7fa;
}
</style>